import React from 'react';

import { gql } from '@apollo/client';

import { ActivityLogEmployeeSlackMessageSent_ActivityLogFragment, ActorType } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import logError from 'src/utils/logError';

import ActivityLogBase from '../../Helpers/ActivityLogBase';
import { useAiAgentLabel } from '../../Helpers/useAiAgentLabel';
import ActivityLogFeedSlackMessage from '../ActivityLogFeedSlackMessage';

interface Fragments {
  activityLog: ActivityLogEmployeeSlackMessageSent_ActivityLogFragment;
}

type EmployeeSlackMessageSentDetails = {
  __typename?: 'ActivityLogEmployeeSlackMessageSentDetails';
  employeeName: string;
  body: string;
};

const ActivityLogEmployeeSlackMessageSent: FCWithFragments<Fragments> = ({ activityLog }) => {
  const aiAgentLabel = useAiAgentLabel();
  const details = activityLog.details as unknown as EmployeeSlackMessageSentDetails | null | undefined;

  // eslint-disable-next-line no-underscore-dangle
  if (details?.__typename !== 'ActivityLogEmployeeSlackMessageSentDetails') {
    logError('ActivityLogEmployeeSlackMessageSent: details is not ActivityLogEmployeeSlackMessageSentDetails');
    return null;
  }

  const { body, employeeName: recipientName } = details;
  const actorName =
    activityLog.activityActorType === ActorType.Agent
      ? aiAgentLabel
      : (activityLog.actorEmployee?.fullName ?? 'Someone');

  return (
    <ActivityLogBase
      activityLog={activityLog}
      actorName={actorName}
      actionLabel={`sent ${recipientName} a Slack message`}
    >
      <ActivityLogFeedSlackMessage fromName={actorName} toName={recipientName} body={body} />
    </ActivityLogBase>
  );
};

ActivityLogEmployeeSlackMessageSent.fragments = {
  activityLog: gql`
    ${ActivityLogBase.fragments.activityLog}
    fragment ActivityLogEmployeeSlackMessageSent_activityLog on ActivityLog {
      id
      activityType
      details {
        ... on ActivityLogEmployeeSlackMessageSentDetails {
          __typename
          employeeId
          employeeName
          channelId
          threadTs
          messageTs
          body
        }
      }
      ...ActivityLogBase_activityLog
    }
  `,
};

export default ActivityLogEmployeeSlackMessageSent;
